import { XIcon } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Button, buttonStyles, cn } from '@skupreme/shared/ui';
import { FooterRow } from '@elements/FooterAlert';
import { useAuth } from '@contexts/auth';
import { Tabs } from '@utility/constants/common';

type SubscriptionBannerProps = {
  subscription?: {
    name?: string;
    expirationDays?: number;
    trial?: boolean;
  } | null;
  viewPlan: () => void;
};

/** Days left in the trial or plan, along the bottom; the X hides it until reload. */
export function SubscriptionBanner({
  subscription,
  viewPlan,
}: SubscriptionBannerProps) {
  const { setFooterActive } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  const hidden = dismissed || !subscription;

  useEffect(() => {
    if (hidden) return;
    setFooterActive(true);
    return () => setFooterActive(false);
  }, [hidden, setFooterActive]);

  /* No days to count without the subscription. */
  if (hidden) return null;

  const { name, expirationDays = 0, trial = false } = subscription ?? {};
  const plan = trial ? 'Free Trial' : `${name} plan`;

  return (
    <FooterRow
      className={cn(
        'sk:z-[1000] sk:bg-primary sk:py-[13px] sk:pr-[75px] sk:pl-[30px] sk:min-[769px]:pr-[100px]',
        trial && 'sk:pr-6 sk:min-[769px]:pr-6',
      )}
    >
      <div className="sk:flex sk:flex-1 sk:items-center sk:justify-between">
        <div className="sk:text-[15px] sk:leading-5 sk:font-bold">
          {expirationDays < 1
            ? `Less than 1 Days Left in ${plan}`
            : `${expirationDays} Days Left in ${plan}`}
        </div>

        <div className="sk:flex sk:items-center sk:gap-4">
          <Link
            to={`/app/settings/?tab=${Tabs.settings.subscription}`}
            onClick={() => viewPlan()}
            className={buttonStyles({ variant: 'pill' })}
          >
            View Plan
          </Link>
          <Button
            variant="ghost"
            shape="circle"
            aria-label="Hide"
            onPress={() => setDismissed(true)}
            className="sk:text-white sk:hover:text-white"
          >
            <XIcon size={16} />
          </Button>
        </div>
      </div>
    </FooterRow>
  );
}
